import { Controller } from "@hotwired/stimulus"

// The Owner's Reps tab panel (James / Phelipe 2026-10-20): one sub-tab per
// company, an Add users pop-up (several emails at once, checked against the
// company's email domains before anything is sent), and removing a person
// from the owner's projects. Saves go by fetch and the panel's turbo-frame
// reloads — never the whole page.
export default class extends Controller {
  static targets = ["subtab", "section", "addModal", "addTitle", "addHint", "addDomains", "addEmails", "addError", "addSubmit"]
  static values = { createUrl: String, destroyUrl: String, resendUrl: String }

  pick(event) {
    const rep = event.currentTarget.dataset.rep
    this.subtabTargets.forEach(b => this._paint(b, b.dataset.rep === rep))
    this.sectionTargets.forEach(s => s.classList.toggle("hidden", s.dataset.rep !== rep))
  }

  // ── Add users pop-up ────────────────────────────────────────────────
  openAddUsers(event) {
    const d = event.currentTarget.dataset
    this._add = { rep: d.rep, company: d.company, domains: (d.domains || "").split(",").map(s => s.trim().toLowerCase()).filter(Boolean),
                  domainsText: d.domainsText || "", projects: d.projects || "0" }
    this.addTitleTarget.textContent = this._t("addTitle", { company: d.company })
    this.addHintTarget.textContent = this._t("addHint", { company: d.company, count: this._add.projects })
    this.addDomainsTarget.textContent = this._add.domains.length ? this._t("domainsRequired", { domains: this._add.domainsText }) : ""
    this.addDomainsTarget.classList.toggle("hidden", !this._add.domains.length)
    this.addEmailsTarget.value = ""
    this._hideError()
    this.countEmails()
    this.addModalTarget.classList.remove("hidden")
    this.addEmailsTarget.focus()
  }

  closeAddUsers() {
    this.addModalTarget.classList.add("hidden")
  }

  countEmails() {
    const n = this._emails().length
    const btn = this.addSubmitTarget
    btn.textContent = n > 1 ? btn.dataset.labelMany.replace("%{count}", n) : btn.dataset.labelOne
  }

  async submitAddUsers() {
    const emails = this._emails()
    if (!emails.length) { this._showError(this._t("noEmails")); return }
    const bad = emails.filter(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
    if (bad.length) { this._showError(this._t("invalid", { list: bad.join(", ") })); return }
    const blocked = this._add.domains.length ? emails.filter(e => !this._add.domains.includes((e.split("@")[1] || "").toLowerCase())) : []
    if (blocked.length) { this._showError(this._t("blocked", { domains: this._add.domainsText, list: blocked.join(", ") })); return }

    const btn = this.addSubmitTarget
    btn.disabled = true
    try {
      const resp = await fetch(this.createUrlValue.replace("REP_ID", this._add.rep), {
        method: "POST", headers: this._headers(), body: JSON.stringify({ emails })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        const list = (data.errors || []).map(e => e.error)
        this._showError(list.length ? list.join("\n") : (data.error || "Couldn't add the users."))
        return
      }
      this.closeAddUsers()
      this._toast(data.message || "Added.", "notice")
      if ((data.errors || []).length) this._toast(this._t("someFailed", { list: data.errors.map(e => e.error).join(" ") }), "alert")
      this._reload()
    } catch (err) {
      this._showError(err.message || "Couldn't add the users.")
    } finally {
      btn.disabled = false
    }
  }

  // Two clicks: the × becomes "Remove?" first, so one slip can't drop
  // someone from every project.
  // The same welcome email (with the sign-up link) the Team page resends.
  async resendInvite(event) {
    const btn = event.currentTarget
    btn.disabled = true
    try {
      const url = this.resendUrlValue.replace("REP_ID", btn.dataset.rep).replace("USER_ID", btn.dataset.user)
      const resp = await fetch(url, { method: "POST", headers: this._headers() })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't resend.")
      this._toast(data.message || "Invite sent again.", "notice")
    } catch (err) {
      this._toast(err.message || "Couldn't resend.", "alert")
    } finally {
      btn.disabled = false
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── helpers ─────────────────────────────────────────────────────────
  _emails() {
    return [...new Set(this.addEmailsTarget.value.split(/[\s,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean))]
  }

  _showError(text) {
    this.addErrorTarget.textContent = text
    this.addErrorTarget.classList.remove("hidden")
  }

  _hideError() {
    this.addErrorTarget.textContent = ""
    this.addErrorTarget.classList.add("hidden")
  }

  // Wording comes from the panel's data-i18n-* attributes (set in the view)
  // so every language reads its own text; English is the fallback.
  _t(key, vars = {}) {
    const fallbacks = {
      addTitle: "Add users to %{company}",
      addHint: "Each person is created as a %{company} user, gets the invite email, and is put on every project where %{company} is Owner's Rep (%{count} today, and new ones automatically).",
      domainsRequired: "Emails must be %{domains}.",
      noEmails: "Enter at least one email address.",
      invalid: "These aren't valid emails: %{list}",
      blocked: "These don't match %{domains}: %{list}",
      someFailed: "Not added: %{list}"
    }
    let text = this.element.dataset[`i18n${key.charAt(0).toUpperCase()}${key.slice(1)}`] || fallbacks[key]
    Object.entries(vars).forEach(([k, v]) => { text = text.split(`%{${k}}`).join(v) })
    return text
  }

  _reload() {
    const frame = this.element.closest("turbo-frame")
    if (frame && typeof frame.reload === "function") frame.reload()
  }

  _paint(btn, active) {
    btn.classList.toggle("text-brand", active)
    btn.classList.toggle("font-bold", active)
    btn.classList.toggle("text-text-muted", !active)
    btn.classList.toggle("font-medium", !active)
  }

  _headers() {
    return {
      "Content-Type": "application/json", "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _toast(message, type) {
    const el = document.createElement("div")
    el.className = `flash-toast-${type === "alert" ? "alert" : "notice"}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "6000")
    el.textContent = message
    document.body.prepend(el)
  }
}
